import React, { FC, useState } from "react";
import {
  CheckCircle,
  Circle,
  Award,
  AlertCircle,
  BookOpen,
  X,
  ZoomIn,
  Eye,
} from "lucide-react";
import { FaQuestionCircle } from "react-icons/fa";
import {
  ExamsDetailsTypes,
  MarksDetails,
} from "../../interfaces/exams.interface";
import { base64ToString } from "../../utils";
import { Dialog, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { log } from "node:console";

interface QuizQuestion {
  qid: number;
  selected_options_value: string;
  id: number;
  selected_answer: string;
  alloted_marks: number;
  negetive_mark: number;
  question: string;
  correct_answer: string;
  correct_options_value: string;
  marks: number;
  correct_flag: boolean;
  question_image_url?: string;
  options: {
    [key: string]: {
      text: string;
      image_url?: string;
    };
  };
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function ZoomModal({ isOpen, onClose, content }: any) {
  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      className="p-0"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <Eye className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {content.type === "question" ? "Question Details" : `Option`}
              </h3>
              <p className="text-sm text-gray-500">Detailed view</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {content.image && (
            <div className="mb-6">
              <img
                src={content.image}
                alt={content.type === "question" ? "Question" : "Option"}
                className="w-full !min-h-[300px] object-contain rounded-xl border border-gray-200 shadow-sm"
              />
            </div>
          )}

          <div className="prose prose-gray max-w-none">
            <p className="text-base leading-relaxed text-gray-800 whitespace-pre-wrap">
              {content.text}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

type PaperWiseMarksDistributionProps = {
  item: any;
  selectedAnswer: any;
  setSelectedAnswer: React.Dispatch<React.SetStateAction<any>>;
  examsDetailsTypes?: ExamsDetailsTypes | null;
  examStatus?: string;
  qIndex?: number;
  onlyAnswerKeys?:boolean;
};
const PaperWiseMarksDistribution: FC<PaperWiseMarksDistributionProps> = ({
  item,
  selectedAnswer,
  examsDetailsTypes,
  setSelectedAnswer,
  examStatus,
  qIndex,
  onlyAnswerKeys
}) => {
  
  
  
  const [zoomModal, setZoomModal] = useState<{
    isOpen: boolean;
    content: {
      type: "question" | "option";
      text: string;
      image?: string;
      optionKey?: string;
    };
  }>({
    isOpen: false,
    content: { type: "question", text: "" },
  });
  const openZoom = (
    type: "question" | "option",
    text: string,
    image?: string,
    optionKey?: string
  ) => {
    setZoomModal({
      isOpen: true,
      content: { type, text, image, optionKey },
    });
  };

  const closeZoom = () => {
    setZoomModal({ isOpen: false, content: { type: "question", text: "" } });
  };

  const getCorrectOption = (item: any) => {
    // const isCorrect =
    //   item[optionKey] === item["option_actual_ancer_id"] &&
    //   examStatus &&
    //   examStatus?.toLowerCase() === "published";

    if (item?.option_a_id === item?.option_actual_ancer_id) {
      return {
        selected_option_text: item?.option_a,
        selected_option_image: item?.option_a_img,
      };
    }

    if (item?.option_b_id === item?.option_actual_ancer_id) {
      return {
        selected_option_text: item?.option_b,
        selected_option_image: item?.option_b_img,
      };
    }
    if (item?.option_c_id === item?.option_actual_ancer_id) {
      return {
        selected_option_text: item?.option_c,
        selected_option_image: item?.option_c_img,
      };
    }
    if (item?.option_d_id === item?.option_actual_ancer_id) {
      return {
        selected_option_text: item?.option_d,
        selected_option_image: item?.option_d_img,
      };
    }
  };

  const getSelectedOption = (item: any) => {
    // const isCorrect =
    //   item[optionKey] === item["option_actual_ancer_id"] &&
    //   examStatus &&
    //   examStatus?.toLowerCase() === "published";

    if (item?.option_a_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_a,
        selected_option_image: item?.option_a_img,
      };
    }

    if (item?.option_b_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_b,
        selected_option_image: item?.option_b_img,
      };
    }
    if (item?.option_c_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_c,
        selected_option_image: item?.option_c_img,
      };
    }
    if (item?.option_d_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_d,
        selected_option_image: item?.option_d_img,
      };
    }
  };

  const getOptionClasses = (item: any, optionKey: string) => {
    const isSelected = item[optionKey] === item["selected_option_id"];
    const isCorrect =
      item[optionKey] === item["option_actual_ancer_id"] &&
      examStatus &&
      examStatus?.toLowerCase() === "published";

    if (isSelected && isCorrect) {
      return "bg-gradient-to-r from-green-100 to-emerald-100 border-green-300 text-green-900 shadow-sm";
    } else if (isSelected && !isCorrect) {
      return "bg-gradient-to-r from-blue-100 to-indigo-100 border-blue-300 text-blue-900 shadow-sm";
    } else if (!isSelected && isCorrect) {
      return "bg-gradient-to-r from-green-100 to-green-100 border-green-200 text-green-800";
    }
    return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200 text-gray-700 hover:border-gray-300";
  };

  const getOptionIcon = (item: any, optionKey: string) => {
    const isSelected = item[optionKey] === item["selected_option_id"];
    const isCorrect = item[optionKey] === item["option_actual_ancer_id"];

    if (isSelected && isCorrect) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    } else if (isSelected && !isCorrect) {
      return <Circle className="w-4 h-4 text-blue-600 fill-current" />;
    } else if (!isSelected && isCorrect) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <Circle className="w-4 h-4 text-gray-400" />;
  };

  return (
    <div
      key={item.qid}
      className="bg-white/80 backdrop-blur-sm !my-4  cursor-pointer rounded-2xl  border !border-gray-300 border-white/20 hover:shadow-xl transition-all duration-300"
    >
      {item.obtain_marks &&
      item.selected_option_id &&
      item.question_marks &&
      Number(item.obtain_marks) < Number(item.question_marks) &&
      examStatus &&
      !onlyAnswerKeys  && 
      examStatus?.toLowerCase() === "published" ? (
        <div
          onClick={() => {
            if (
              item.selected_option_id &&
              item.obtain_marks &&
              item.question_marks &&
              Number(item.obtain_marks) < Number(item.question_marks) &&
              examStatus &&
              examStatus?.toLowerCase() === "published"
            )
              setSelectedAnswer(item);
          }}
          className={`absolute bottom-0 right-0 py-1 px-2 text-[13px]  text-white font-semibold rounded-tl-lg ${
            selectedAnswer?.id === item?.id ? `bg-blue-600` : `bg-[#3b82f6]`
          } `}
        >
          {selectedAnswer?.question_id === item?.question_id
            ? `Answer Selected For Objection`
            : `Click Here For Objection`}
        </div>
      ) : (
        <></>
      )}
      {/* Compact item Header */}
      <div className="p-4 border-b border-gray-100/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {qIndex !== undefined && qIndex >= 0 ? (
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl !px-[2px] flex items-center justify-center !text-white text-sm font-bold">
                {qIndex ? qIndex + 1 : "1"}
              </div>
            ) : (
              <></>
            )}

            {/* <div
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                item.correct_flag
                  ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200"
                  : "bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200"
              }`}
            >
              {item.correct_flag ? "✓ Correct" : "✗ Incorrect"}
            </div> */}
          </div>

          <div className="flex items-center space-x-4 text-xs">
            {item.question_marks ? (
              <div className="flex items-center space-x-2 bg-amber-600 px-3  rounded-sm py-1 ">
                <Award className="w-4 h-4 text-white" />
                <p className="text-xs text-white">Allotted Marks : </p>
                <span className="font-medium text-white">
                  {item.question_marks}
                </span>
              </div>
            ) : (
              <></>
            )}

            {item.question_negative_mark ? (
              <div className="flex items-center space-x-2 bg-red-500 px-3 rounded-sm py-1">
                <AlertCircle className="w-4 h-4 text-white" />
                         <p className="text-xs text-white">Negative Marks : </p>
                <span className="font-medium text-white">
                  {item.question_negative_mark}
                </span>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
        {examStatus &&
        examStatus?.toLowerCase() === "published" &&
        item.obtain_marks ? (
          <div className="flex mt-2 mb-1 ">
            <div
              className={`flex mt-2  text-xs px-2 py-1 font-semibold text-white rounded-sm ${
                item.obtain_marks?.startsWith("-")
                  ? `bg-red-600`
                  : `bg-green-500`
              }`}
            >
              Obtained Marks : {item.obtain_marks}
            </div>
          </div>
        ) : (
          <></>
        )}
            
      </div>

      {/* Question Content */}
      <div className="p-4">
        {/* Question with Zoom */}
        <div className="mb-4">
          <div className="flex flex-col gap-y-4">
            {item.question_text?.trim()?.length ? (
              <div className="flex-1">
                <div className="flex items-start space-x-3 px-2  ">
                  <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center !text-white text-sm font-bold">
                    <FaQuestionCircle className="text-white" size={15} />
                  </div>
                  <p className="text-sm font-medium text-gray-900 leading-relaxed ">
                    {item.question_text}
                  </p>

                  <button
                    onClick={() =>
                      openZoom(
                        "question",
                        item.question_text,
                        item.question_img
                      )
                    }
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-lg"
                    title="View full question"
                  >
                    <ZoomIn className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ) : (
              <></>
            )}

            {item.question_img && (
              <div className="w-full flex items-center gap-x-4">
                <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center !text-white text-sm font-bold">
                  <FaQuestionCircle className="text-white" size={15} />
                </div>
                <img
                  // src={item.question_image_url}
                  src={base64ToString(`${item.question_img}`)}
                  alt="Question"
                  className="!w-full !h-[100px] object-contain rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() =>
                    openZoom(
                      "question",
                      item.question_text,
                      base64ToString(`${item.question_img}`)
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2  gap-x-4 gap-y-4 mb-3">
          {item?.option_a || item?.option_a_img ? (
            <div
              className={`p-3 rounded-xl border transition-all duration-200 group    ${getOptionClasses(
                item,
                "option_a_id"
              )}`}
            >
              <div className="flex items-center gap-x-4">
                {item?.option_a?.trim()?.length ? (
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-sm text-wrap !text-black">
                      {item?.option_a}
                    </span>
                  </div>
                ) : (
                  <></>
                )}

                <div className="flex items-center space-x-2  cursor-pointer ">
                  {item.option_a_img && (
                    <>
                      <img
                        src={`${base64ToString(`${item.option_a_img}`)}`}
                        alt={`Option image`}
                        className="!w-[80%] !h-16 object-contain rounded border"
                      />
                      <ZoomIn
                        className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() =>
                          openZoom(
                            "option",
                            item?.option_a,
                            `${base64ToString(`${item.option_a_img}`)}`,
                            "option_a_img"
                          )
                        }
                      />
                    </>
                  )}

                  {/* {getOptionIcon(item, optionKey)} */}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}

          {item?.option_b || item?.option_b_img ? (
            <div
              className={`p-3 rounded-xl border transition-all duration-200 group   ${getOptionClasses(
                item,
                "option_b_id"
              )}`}
              // onClick={() =>
              //   openZoom(
              //     "option",
              //     optionData.text,
              //     optionData.image_url,
              //     optionKey
              //   )
              // }
            >
              <div className="flex items-center gap-x-4">
                {item?.option_b?.trim()?.length ? (
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-sm text-wrap">{item?.option_b}</span>
                  </div>
                ) : (
                  <></>
                )}
                <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                  {item.option_b_img ? (
                    <>
                      <img
                        src={`${base64ToString(`${item.option_b_img}`)}`}
                        alt={`Option image`}
                        className="!w-[80%] !h-16 object-contain rounded border"
                      />
                      <ZoomIn
                        className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() =>
                          openZoom(
                            "option",
                            item?.option_b,
                            `${base64ToString(`${item.option_b_img}`)}`,
                            "option_b_img"
                          )
                        }
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  {/* {getOptionIcon(item, optionKey)} */}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}

          {item?.option_c || item?.option_c_img ? (
            <div
              className={`p-3 rounded-xl border transition-all duration-200 group   ${getOptionClasses(
                item,
                "option_c_id"
              )}`}
              // onClick={() =>
              //   openZoom(
              //     "option",
              //     optionData.text,
              //     optionData.image_url,
              //     optionKey
              //   )
              // }
            >
              <div className="flex items-center gap-x-4">
                {item?.option_c?.trim()?.length ? (
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-sm text-wrap">{item?.option_c}</span>
                  </div>
                ) : (
                  <></>
                )}
                <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                  {item.option_c_img ? (
                    <>
                      <img
                        src={`${base64ToString(`${item.option_c_img}`)}`}
                        alt={`Option image`}
                        className="!w-[80%] !h-16 object-contain rounded border"
                      />
                      <ZoomIn
                        className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() =>
                          openZoom(
                            "option",
                            item?.option_c,
                            `${base64ToString(`${item.option_c_img}`)}`,
                            "option_c_img"
                          )
                        }
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  {/* {getOptionIcon(item, optionKey)} */}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}

          {item?.option_d || item?.option_d_img ? (
            <div
              className={`p-3 rounded-xl border transition-all duration-200 group    ${getOptionClasses(
                item,
                "option_d_id"
              )}`}
              // onClick={() =>
              //   openZoom(
              //     "option",
              //     optionData.text,
              //     optionData.image_url,
              //     optionKey
              //   )
              // }
            >
              <div className="flex items-center gap-x-4">
                {item?.option_d?.trim()?.length ? (
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-sm text-wrap">{item?.option_d}</span>
                  </div>
                ) : (
                  <></>
                )}

                <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                  {item.option_d_img ? (
                    <>
                      <img
                        src={`${base64ToString(`${item.option_d_img}`)}`}
                        alt={`Option image`}
                        className="!w-[80%] !h-16 object-contain rounded border"
                      />
                      <ZoomIn
                        className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() =>
                          openZoom(
                            "option",
                            item?.option_d,
                            `${base64ToString(`${item.option_d_img}`)}`,
                            "option_d_img"
                          )
                        }
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  {/* {getOptionIcon(item, optionKey)} */}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>

         {
        !item.selected_option_id ? (
          <div className="flex mt-2 mb-1 w-full   ">
            <div
              className={`flex mt-2  text-xs px-2 py-1 font-semibold text-white rounded-sm bg-blue-700 `}
            >
              Not Attempted 
            </div>
          </div>
        ) : (
          <></>
        )}

        {/* Compac
          }t Answer Summary */}
        <div className="flex items-center justify-between text-xs bg-gray-50/50 rounded-xl p-3">
          <div className="flex flex-col gap-y-4">
            {getSelectedOption(item) ? (
              <div className="flex flex-col gap-y-3">
                <div className="flex items-center gap-x-4">
                  <Circle className="w-3 h-3 text-blue-600 fill-current" />
                  <p className="text-md text-blue-600  font-bold">Selected Answer</p>
                </div>

                <div
                  className={`p-3 rounded-xl border transition-all duration-200 group   `}
                  // onClick={() =>
                  //   openZoom(
                  //     "option",
                  //     optionData.text,
                  //     optionData.image_url,
                  //     optionKey
                  //   )
                  // }
                >
                  <div className="flex items-center gap-x-4">
                    {getSelectedOption(item)?.selected_option_text?.trim()
                      ?.length ? (
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <span className="text-sm text-wrap">
                          {getSelectedOption(item)?.selected_option_text}
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                      {getSelectedOption(item)?.selected_option_image ? (
                        <>
                          <img
                            src={`${base64ToString(
                              `${
                                getSelectedOption(item)?.selected_option_image
                              }`
                            )}`}
                            alt={`Option image`}
                            className="!w-[80%] !h-16 object-contain rounded border"
                          />
                          <ZoomIn
                            className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() =>
                              openZoom(
                                "option",
                                item?.option_c,
                                `${base64ToString(
                                  `${
                                    getSelectedOption(item)
                                      ?.selected_option_image
                                  }`
                                )}`,
                                "option_c_img"
                              )
                            }
                          />
                        </>
                      ) : (
                        <></>
                      )}

                      {/* {getOptionIcon(item, optionKey)} */}
                    </div>
                  </div>
                </div>

                <div></div>

                {/* <span className="text-gray-600">
                Selected:{" "}
                <span className="font-semibold text-blue-700">
                  {item.selected_options_value}
                </span>
              </span> */}
              </div>
            ) : (
              <></>
            )}
            {examStatus && examStatus?.toLowerCase() === "published" ? (
              <div className="flex flex-col gap-y-3">
                <div className="flex items-center gap-x-4">
                  <CheckCircle className="w-3 h-3 text-green-600 fill-current" />
                  <p className="text-md font-bold text-green-600">
                    Correct Answer
                  </p>
                </div>
                {getCorrectOption(item) ? (
                  <div
                    className={`p-3 rounded-xl border transition-all duration-200 group   `}
                    // onClick={() =>
                    //   openZoom(
                    //     "option",
                    //     optionData.text,
                    //     optionData.image_url,
                    //     optionKey
                    //   )
                    // }
                  >
                    <div className="flex items-center gap-x-4">
                      {getCorrectOption(item)?.selected_option_text?.trim()
                        ?.length ? (
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <span className="text-sm text-wrap">
                            {getCorrectOption(item)?.selected_option_text}
                          </span>
                        </div>
                      ) : (
                        <></>
                      )}
                      <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                        {getCorrectOption(item)?.selected_option_image ? (
                          <>
                            <img
                              src={`${base64ToString(
                                `${
                                  getCorrectOption(item)?.selected_option_image
                                }`
                              )}`}
                              alt={`Option image`}
                              className="!w-[90%] !h-16 object-contain rounded border"
                            />
                            <ZoomIn
                              className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() =>
                                openZoom(
                                  "option",
                                  item?.option_c,
                                  `${base64ToString(
                                    `${
                                      getCorrectOption(item)
                                        ?.selected_option_image
                                    }`
                                  )}`,
                                  "option_c_img"
                                )
                              }
                            />
                          </>
                        ) : (
                          <></>
                        )}

                        {/* {getOptionIcon(item, optionKey)} */}
                      </div>
                    </div>
                  </div>
                ) : (
                  <></>
                )}
                <div></div>

                {/* <span className="text-gray-600">
                Selected:{" "}
                <span className="font-semibold text-blue-700">
                  {item.selected_options_value}
                </span>
              </span> */}
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <ZoomModal
        isOpen={zoomModal.isOpen}
        onClose={closeZoom}
        content={zoomModal.content}
      />
    </div>
  );
};

export default PaperWiseMarksDistribution;

//   return (
//     <div
//       key={item.id}
//       onClick={() => {
//         if (item.selected_answer && item.marks < item.alloted_marks)
//           setSelectedAnswer(item);
//       }}
//       className={`mb-4 lg:p-4 pt-2 px-3 pb-8  relative  border rounded-lg transition-transform transform  ${
//         item.marks <= 0
//           ? "hover:bg-red-100 bg-white"
//           : "hover:bg-green-100 bg-white"
//       } ${
//         selectedAnswer?.id === item?.id
//           ? `border border-l-4   border-emerald-300`
//           : ``
//       }
//                             ${
//                               item.selected_answer &&
//                               item.marks < item.alloted_marks
//                                 ? `cursor-pointer`
//                                 : ``
//                             }
//                             `}
//     >
//       <p className="font-semibold">{item.question}</p>
//       <div
//       className='flex flex-col lg:flex-row mt-2'
//         // style={{
//         //   display: "flex",
//         //   flexDirection: "row",
//         //   marginTop: "2px",
//         // }}
//       >
//         <p>
//           <span className="font-medium">Alloted Marks: </span>
//           {item.alloted_marks}
//         </p>
//         <p className='lg:ml-5'>
//           <span className="font-medium">Negative Marks: </span>
//           {item.negetive_mark}
//         </p>
//       </div>

//       <p className="mt-1">
//         <span className="font-medium">Selected Answer: </span>
//         {/* {item.selected_options_value} */}
//         {item.selected_answer}
//       </p>
//       <p className="mt-1">
//         <span className="font-medium">Correct Answer: </span>
//         {/* {item.correct_options_value} */}
//         {item.correct_answer}
//       </p>
//       <p
//         className={`mt-1 font-medium ${
//           item.marks <= 0 ? "text-red-500" : "text-green-500"
//         }`}
//       >
//         Marks: {item.marks}
//       </p>
//       {item.selected_answer && item.marks < item.alloted_marks ? (
//         <div
//           className={`absolute bottom-0 right-0 py-1 px-2 text-[13px]  text-white font-semibold rounded-tl-lg ${
//             selectedAnswer?.id === item?.id ? `bg-emerald-400` : `bg-[#3b82f6]`
//           } `}
//         >
//           {selectedAnswer?.id === item?.id
//             ? `Answer Selected For Objection`
//             : `Click Answer For Objection`}
//         </div>
//       ) : (
//         <></>
//       )}
//     </div>
//   );
// }
